import { useMutation } from '@apollo/client/react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import BaseButton from 'src/components/Buttons/BaseButton'
import BaseTextField from 'src/components/Inputs/BaseTextField'
import PhoneField from 'src/components/Inputs/PhoneField'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import {
  DirectoryRevealChannel,
  DirectoryRevealStatus,
  ICoordinates,
  IDirectoryContact,
  IMutation,
  IMutationRevealDirectoryContactArgs
} from 'src/types/graphql'
import { RedirectUrls } from 'src/types/types'
import { verifyBotId } from 'src/utils/botid'
import {
  DirectoryRevealSurface,
  trackDirectoryRecoveryClick,
  trackDirectoryReveal
} from 'src/utils/pixels/directoryEvents'
import { safeStorageGet, safeStorageSet } from 'src/utils/safeStorage'
import { buildWhatsappUrl } from 'src/utils/whatsapp'
import ContactResult from './ContactResult'
import { REVEAL_DIRECTORY_CONTACT } from './directoryQuery'

/**
 * `blue-800`. The brand `blue-500` (#00A4FF) is 2.71:1 against white and
 * 2.5–2.7:1 as text on cream — both below WCAG AA's 4.5:1. Directory surfaces
 * use the darker shade for text and solid CTAs; tints and hairlines keep
 * blue-500 so the surfaces still read as SpotMe.
 */
const ACCESSIBLE_BLUE = '#005E92'

/** Cap the BotID pre-flight so a slow edge check never blocks the submit. */
const BOTID_SUBMIT_WAIT_MS = 2500

/**
 * Remembered capture. Scoped keys, not a blob: the channel and the value are
 * read independently and a corrupt half degrades to "ask again", which is the
 * correct default for a storage read that may legitimately return null.
 */
const STORAGE_CHANNEL_KEY = 'directory:reveal:channel'
const STORAGE_CONTACT_KEY = 'directory:reveal:contact'

type FormValues = { contact: string }

/**
 * `FIELD_ID` must equal the react-hook-form field name: `BaseTextField` renders
 * `<Controller name={id} …>`, so a decorative id registers a PHANTOM field —
 * `errors.contact` stays undefined (the error `<p>` below never renders and
 * `aria-invalid` never flips) and `onSubmit` destructures a `contact` nothing
 * ever wrote, posting an empty string. That broke the EMAIL reveal path
 * outright; caught by the a11y review 2026-08-23. The id doubles as the stable
 * SSR input id, which is what it was introduced for.
 */
const FIELD_ID = 'contact'
const ERROR_ID = 'directory-reveal-contact-error'

interface RevealContactProps {
  locationId: string
  /** Where to send the renter when the facility's month is exhausted. `null`
   *  when the area has no verified SpotMe supply — the capped state then
   *  offers the corporate WhatsApp instead of a page with nothing on it. */
  verifiedNearbyHref: string | null
  /** The facility's pin — the revealed bundle turns it into a directions link. */
  coordinates?: ICoordinates | null
  /** Analytics surface — required, never defaulted (instrumentation rule). */
  surface: DirectoryRevealSurface
  /** A deeper level when the form opens inside a card — the city guide nests
   *  it under the card's h3. */
  headingLevel?: 'h2' | 'h3' | 'h4'
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * The one-field contact reveal (plan §2.4).
 *
 * Rules this encodes:
 *   · ONE field — WhatsApp or email, the renter picks. Never a login: an
 *     unclaimed facility's phone number is not worth an account, and a hard
 *     gate would break the encyclopedia promise the whole directory rests on.
 *   · Logged-in renters are zero-friction — the field is PREFILLED from their
 *     account, but still requires a deliberate tap. Auto-revealing on mount
 *     would spend one of the facility's three monthly slots on every logged-in
 *     pageview, i.e. three passers-by would exhaust a real business's month.
 *   · Remembered through `safeStorage` — reading `localStorage` itself throws
 *     on iOS private browsing, and a lost prefill must degrade to "type it
 *     again", never to a crash.
 *   · CAPPED is a routing state, not an error. The renter is handed verified
 *     supply nearby — cap the value, never withhold it outright.
 *   · The contact is never logged and never enters an analytics event; only
 *     the channel does.
 *   · SpotMe STAFF get no form at all. An admin is not demand: the server
 *     answers an admin reveal read-only (no ledger write, no lead, no cap
 *     spent), so there is nothing to capture and no reason to make Jules type
 *     his own phone number to read an operator's. The bundle is fetched on
 *     mount and labelled as the internal view so it is never mistaken for
 *     what a renter sees. No `directory_reveal` event either — the pixel
 *     wrappers already drop internal traffic, but a staff reveal is not a
 *     reveal, and that should be visible HERE rather than inferred from a
 *     guard three files away.
 */
const RevealContact = ({
  locationId,
  verifiedNearbyHref,
  coordinates,
  surface,
  headingLevel = 'h2'
}: RevealContactProps) => {
  const Heading = headingLevel
  const { t } = useTranslation('directorio')
  const logger = useLogger()
  const { currentUser } = useAuthProvider()
  // `adminRole` OR `isAdmin` — the same predicate the server's
  // `isDirectoryAdminViewer` and `EventService.createEvent` use. Checking only
  // `isAdmin` would leave role-only admins looking at the renter form while
  // the server already treats their reveal as read-only.
  //
  // `currentUser` resolves one round-trip after hydration, so an admin sees
  // the renter form for a beat before it swaps. Accepted: gating the form on
  // an auth round-trip would delay every anonymous renter to spare five
  // people a flicker.
  const isStaff = !!currentUser?.isAdmin || !!currentUser?.adminRole

  const [channel, setChannel] = useState<DirectoryRevealChannel>(
    DirectoryRevealChannel.Whatsapp
  )
  const [contactBundle, setContactBundle] = useState<IDirectoryContact | null>(
    null
  )
  const [capped, setCapped] = useState(false)
  const [remaining, setRemaining] = useState<number | null>(null)
  const [failed, setFailed] = useState(false)

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    unregister,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: { contact: '' },
    mode: 'onTouched'
  })
  // The result replaces the form, so the element the user activated is
  // unmounted. Without moving focus the caret lands back on <body> and a
  // screen reader announces nothing at all — the whole payoff of the feature
  // would be silent. `resultRef` receives focus once the result renders.
  const resultRef = useRef<HTMLDivElement>(null)
  // A channel tap hands the renter straight to the field it chose — see the
  // radio group below. The input changes element on a switch (phone ↔ email),
  // so a switch parks the request here until the new field has rendered.
  const fieldRef = useRef<HTMLDivElement>(null)
  const focusAfterSwapRef = useRef(false)
  const focusContactField = useCallback(
    () => fieldRef.current?.querySelector('input')?.focus(),
    []
  )
  useEffect(() => {
    if (!focusAfterSwapRef.current) return
    focusAfterSwapRef.current = false
    focusContactField()
  }, [channel, focusContactField])
  const revealed = capped || !!contactBundle
  useEffect(() => {
    // Only after a DELIBERATE submit. The staff bundle arrives on its own, and
    // moving focus for something nobody activated is WCAG 3.2.1 noise.
    if (revealed && !isStaff) resultRef.current?.focus()
  }, [revealed, isStaff])

  // Prefill, in priority order: what this browser used last, then the signed-in
  // account. Runs after mount — SSR must not branch on either source (both are
  // client-only) or the markup would diverge at hydration.
  //
  // `currentUser` arrives from an async query, so this effect re-runs when it
  // resolves — typically a second or two into the page on mobile. Without the
  // two guards below it would overwrite whatever the renter had already typed
  // in the meantime, and flip their channel back, with no visible cause.
  // `prefilledRef` makes it strictly once-per-mount; the `getValues` check
  // additionally refuses to touch a field that already has content.
  const prefilledRef = useRef(false)
  useEffect(() => {
    if (prefilledRef.current) return
    // Staff render no field, and their account contact is not a lead.
    if (isStaff) return
    if (getValues('contact')) return

    const rememberedChannel = safeStorageGet('local', STORAGE_CHANNEL_KEY)
    const rememberedContact = safeStorageGet('local', STORAGE_CONTACT_KEY)
    if (
      rememberedContact &&
      (rememberedChannel === DirectoryRevealChannel.Email ||
        rememberedChannel === DirectoryRevealChannel.Whatsapp)
    ) {
      prefilledRef.current = true
      setChannel(rememberedChannel)
      setValue('contact', rememberedContact)
      return
    }
    if (currentUser?.phoneNumber) {
      prefilledRef.current = true
      setChannel(DirectoryRevealChannel.Whatsapp)
      setValue('contact', currentUser.phoneNumber)
      return
    }
    if (currentUser?.email) {
      prefilledRef.current = true
      setChannel(DirectoryRevealChannel.Email)
      setValue('contact', currentUser.email)
    }
  }, [
    currentUser?.phoneNumber,
    currentUser?.email,
    isStaff,
    setValue,
    getValues
  ])

  const [reveal, { loading }] = useMutation<
    Pick<IMutation, 'revealDirectoryContact'>,
    IMutationRevealDirectoryContactArgs
  >(REVEAL_DIRECTORY_CONTACT)

  /**
   * Staff path: fetch the bundle with NO capture. Safe to run on mount only
   * because the server answers an admin read-only — the "auto-reveal would
   * burn a slot per pageview" rule above is a rule about RENTERS. The guard is
   * a REF, not state, so a re-render (or `currentUser` refetching) cannot fire
   * a second request.
   */
  const staffFetchedRef = useRef(false)
  const revealAsStaff = useCallback(async () => {
    setFailed(false)
    try {
      const { data } = await reveal({ variables: { input: { locationId } } })
      const result = data?.revealDirectoryContact
      if (!result?.contact) {
        setFailed(true)
        return
      }
      setRemaining(result.revealsRemaining)
      setContactBundle(result.contact)
    } catch (error) {
      logger.warn('Directory staff reveal failed', { error, locationId })
      setFailed(true)
    }
  }, [reveal, locationId, logger])

  useEffect(() => {
    if (!isStaff || staffFetchedRef.current) return
    staffFetchedRef.current = true
    void revealAsStaff()
  }, [isStaff, revealAsStaff])

  const onSubmit = useCallback(
    async ({ contact }: FormValues) => {
      setFailed(false)
      try {
        // Soft-flag bot defense — races a timeout so a slow edge check falls
        // through unflagged rather than holding a renter on a spinner.
        await Promise.race([
          verifyBotId({ surface: 'directory_reveal' }),
          new Promise<undefined>((resolve) =>
            setTimeout(() => resolve(undefined), BOTID_SUBMIT_WAIT_MS)
          )
        ])
        const { data } = await reveal({
          variables: { input: { channel, contact, locationId } }
        })
        const result = data?.revealDirectoryContact
        if (!result) {
          setFailed(true)
          return
        }
        safeStorageSet('local', STORAGE_CHANNEL_KEY, channel)
        safeStorageSet('local', STORAGE_CONTACT_KEY, contact)

        const isCapped = result.status === DirectoryRevealStatus.Capped
        setCapped(isCapped)
        setRemaining(result.revealsRemaining)
        setContactBundle(isCapped ? null : (result.contact ?? null))
        trackDirectoryReveal({
          locationId,
          channel,
          capped: isCapped,
          revealsRemaining: result.revealsRemaining,
          surface
        })
      } catch (error) {
        // Never surface a raw backend message here: the mutation deliberately
        // answers a miss and a non-directory row identically so it can't be
        // used as an existence oracle, and echoing that back would be noise.
        logger.warn('Directory reveal failed', { error, locationId })
        setFailed(true)
      }
    },
    [reveal, channel, locationId, logger, surface]
  )

  /**
   * The internal view. Deliberately LABELLED: an admin looking at a ficha must
   * be able to tell at a glance that this is not the page a renter sees —
   * otherwise "the contact just shows up" reads as a leak rather than a staff
   * affordance, and the monthly cap looks broken.
   */
  if (isStaff) {
    return (
      // No card chrome of its own: `ContactResult` already renders a card, and
      // nesting two would read as a panel inside a panel. The badge sits above.
      <section
        className="flex flex-col gap-3"
        aria-label={t('REVEAL.STAFF_BADGE')}
        data-testid="directory-reveal-staff"
      >
        <p className="inline-flex w-fit items-center gap-2 rounded-full border border-blue-800/25 px-3 py-1 text-[10px] tracking-[0.14em] text-blue-800 uppercase">
          <i className="si si-bulb text-[10px]" aria-hidden="true" />
          {t('REVEAL.STAFF_BADGE')}
        </p>
        {contactBundle ? (
          <ContactResult
            bundle={contactBundle}
            locationId={locationId}
            remaining={remaining}
            coordinates={coordinates}
            isStaff
          />
        ) : (
          <div className="rounded-2xl border border-ink-100/15 bg-cream-300 p-5 md:p-6">
            {failed ? (
              <>
                <p role="alert" className="text-[14px] text-red-800">
                  {t('REVEAL.SUBMIT_FAILED')}
                </p>
                <BaseButton
                  type="button"
                  variant="contained"
                  customColor={ACCESSIBLE_BLUE}
                  loading={loading}
                  onClick={() => void revealAsStaff()}
                  className="mt-3 rounded-full"
                >
                  {t('REVEAL.CTA')}
                </BaseButton>
              </>
            ) : (
              <p className="text-[14px] text-ink-200" role="status">
                {t('REVEAL.STAFF_LOADING')}
              </p>
            )}
          </div>
        )}
      </section>
    )
  }

  if (capped) {
    return (
      <section
        ref={resultRef}
        tabIndex={-1}
        role="status"
        aria-label={t('REVEAL.A11Y_RESULT_REGION')}
        className="rounded-2xl border border-ink-100/15 bg-cream-300 p-5 outline-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 md:p-6"
        data-testid="directory-reveal-capped"
      >
        <Heading className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
          {t('REVEAL.CAPPED_TITLE')}
        </Heading>
        <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
          {verifiedNearbyHref
            ? t('REVEAL.CAPPED_BODY')
            : t('REVEAL.CAPPED_BODY_NO_SUPPLY')}
        </p>
        {verifiedNearbyHref ? (
          <Link
            href={verifiedNearbyHref}
            onClick={() =>
              trackDirectoryRecoveryClick({
                locationId,
                target: 'verified_nearby_capped'
              })
            }
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-800 px-6 py-3 text-base font-medium text-white-500 transition-transform duration-200 hover:translate-x-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
          >
            {t('REVEAL.CAPPED_CTA')}
            <i className="si si-right text-[10px]" aria-hidden="true" />
          </Link>
        ) : (
          <a
            href={buildWhatsappUrl(t('REVEAL.CAPPED_WA_MESSAGE'))}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackDirectoryRecoveryClick({
                locationId,
                target: 'whatsapp_capped'
              })
            }
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-blue-800 px-6 py-3 text-base font-medium text-white-500 transition-transform duration-200 hover:translate-x-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
          >
            {t('REVEAL.CAPPED_WA_CTA')}
            <i className="si si-right text-[10px]" aria-hidden="true" />
          </a>
        )}
      </section>
    )
  }

  if (contactBundle) {
    return (
      <div
        ref={resultRef}
        tabIndex={-1}
        role="status"
        aria-label={t('REVEAL.A11Y_RESULT_REGION')}
        className="rounded-2xl outline-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2"
      >
        <ContactResult
          bundle={contactBundle}
          locationId={locationId}
          remaining={remaining}
          coordinates={coordinates}
        />
      </div>
    )
  }

  const isEmail = channel === DirectoryRevealChannel.Email
  const fieldError = errors.contact

  return (
    <section className="rounded-2xl border border-ink-100/15 bg-cream-300 p-5 md:p-6">
      <Heading className="font-display text-[19px] leading-tight font-medium text-ink-700 md:text-xl">
        {t('REVEAL.TITLE')}
      </Heading>
      <p className="mt-2 text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
        {currentUser ? t('REVEAL.BODY_LOGGED_IN') : t('REVEAL.BODY')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
        <fieldset className="border-0 p-0">
          <legend className="mb-2 text-xs font-medium text-ink-200">
            {t('REVEAL.CHANNEL_LABEL')}
          </legend>
          {/* Two labelled controls side by side — a radio group, so it stays
              one tab stop with arrow-key movement, and stacks nowhere because
              two short pills fit at 320px.
              Read as a CHOICE, not a button (2026-09-26): the pre-selected
              "WhatsApp" pill drew 102 taps from 32 people in 30 days, every
              one a no-op — it looked like "contact them on WhatsApp". Each
              pill now carries a radio dot, and a tap moves the renter to the
              field it chose: the selected pill focuses it at once, a switch
              focuses the new field once it renders. Pointer taps only — arrow
              keys fire their click on the INPUT, and focus must stay in the
              group for them. */}
          <div className="flex gap-2">
            {[
              {
                value: DirectoryRevealChannel.Whatsapp,
                label: t('REVEAL.CHANNEL_WHATSAPP')
              },
              {
                value: DirectoryRevealChannel.Email,
                label: t('REVEAL.CHANNEL_EMAIL')
              }
            ].map((option) => (
              <label
                key={option.value}
                onClick={(event) => {
                  if (event.target instanceof HTMLInputElement) return
                  if (option.value === channel) {
                    // Already checked: skip the label's forwarded click, which
                    // would pull focus back onto the hidden radio.
                    event.preventDefault()
                    focusContactField()
                    return
                  }
                  focusAfterSwapRef.current = true
                }}
                className={`inline-flex cursor-pointer items-center gap-2 rounded-full border py-1.5 pr-4 pl-3 text-sm transition-colors duration-200 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ink-700 ${
                  channel === option.value
                    ? 'border-blue-800 bg-blue-800/10 font-medium text-ink-700'
                    : 'border-ink-100/20 text-ink-200 hover:border-ink-100/40'
                }`}
              >
                <input
                  type="radio"
                  name="directory-reveal-channel"
                  value={option.value}
                  checked={channel === option.value}
                  onChange={() => {
                    setChannel(option.value)
                    // Drop the field's registration BEFORE the swap, rules
                    // included. RHF's `register` SPREADS new options over the
                    // persisted `_f` entry (shouldUnregister defaults false),
                    // so after one toggle the phone path kept the email
                    // `pattern` ("Escribe un correo válido" on a valid phone)
                    // and the email path kept the phone `validate` ("Verifica
                    // la lada" on a valid email) — both fields un-submittable.
                    // Caught live 2026-08-24.
                    unregister('contact')
                    setValue('contact', '')
                  }}
                  className="sr-only"
                />
                <span
                  aria-hidden="true"
                  className={`flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors duration-200 ${
                    channel === option.value
                      ? 'border-blue-800'
                      : 'border-ink-100/40'
                  }`}
                >
                  <span
                    className={`size-2 rounded-full bg-blue-800 transition-transform duration-200 motion-reduce:transition-none ${
                      channel === option.value ? 'scale-100' : 'scale-0'
                    }`}
                  />
                </span>
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="mt-3 flex flex-col gap-2 md:flex-row md:items-start md:gap-3">
          <div ref={fieldRef} className="md:flex-1">
            {/* A REAL <label>, not BaseTextField's `topLabel` — that renders
                as a styled <p> with no `for` and no `aria-labelledby`, so the
                input's accessible name falls through to the placeholder and
                voice control cannot target it. Wrapping the field also gives
                implicit association for PhoneField, which accepts no `id`. */}
            <label htmlFor={isEmail ? FIELD_ID : undefined}>
              <span className="mb-2 block text-[15px] leading-[20px] font-semibold text-black-400">
                {isEmail ? t('REVEAL.LABEL_EMAIL') : t('REVEAL.LABEL_WHATSAPP')}
              </span>
              {isEmail ? (
                // `id` — not `name` — is what BaseTextField feeds to its
                // Controller, and it is also the explicit input id that keeps
                // MUI from minting a useId() that differs across SSR/CSR.
                <BaseTextField
                  id={FIELD_ID}
                  control={control}
                  fullWidth
                  rules={{
                    required: t('REVEAL.ERROR_EMAIL'),
                    pattern: {
                      value: EMAIL_SHAPE,
                      message: t('REVEAL.ERROR_EMAIL')
                    }
                  }}
                  // BaseTextField never reads `fieldState`, so without this the
                  // email path blocks the submit with no border, no message and
                  // aria-invalid=false — a silent dead end.
                  error={!!fieldError}
                  slotProps={{
                    htmlInput: {
                      'aria-describedby': fieldError ? ERROR_ID : undefined,
                      'aria-invalid': !!fieldError
                    }
                  }}
                  placeholder={t('REVEAL.PLACEHOLDER_EMAIL')}
                  autoComplete="email"
                  // 16px on mobile — anything smaller triggers iOS auto-zoom.
                  className="text-base md:text-sm"
                />
              ) : (
                <PhoneField<FormValues>
                  name="contact"
                  control={control}
                  fullWidth
                  rules={{ required: t('REVEAL.ERROR_WHATSAPP') }}
                  placeholder={t('REVEAL.PLACEHOLDER_WHATSAPP')}
                  autoComplete="tel"
                  hideHelper
                />
              )}
            </label>
          </div>
          {/* `contained` — BaseButton's default is MUI's `text` variant, which
              renders the page's ONLY action as bare blue text that reads as a
              link, not a button. */}
          {/* `customColor` is blue-800, not the brand blue-500: white on
              blue-500 is 2.71:1, below the 4.5:1 this button needs as the
              page's primary action. See the contrast note in
              docs/claude/design-language.md. */}
          <BaseButton
            type="submit"
            variant="contained"
            customColor={ACCESSIBLE_BLUE}
            loading={loading}
            className="rounded-full md:mt-8 md:shrink-0"
          >
            {t('REVEAL.CTA')}
          </BaseButton>
        </div>

        {/* PhoneField renders its own message from `fieldState`; BaseTextField
            does not, so the email path needs one here. */}
        {isEmail && fieldError && (
          <p id={ERROR_ID} className="mt-2 text-xs text-red-800">
            {fieldError.message ?? t('REVEAL.ERROR_EMAIL')}
          </p>
        )}

        {/* A SUBMIT failure is not a format error. Telling someone their
            correct address is malformed sends them to fix something that
            isn't broken. */}
        {failed && (
          <p role="alert" className="mt-2 text-xs text-red-800">
            {t('REVEAL.SUBMIT_FAILED')}
          </p>
        )}

        {/* Sentence and link are SEPARATE elements on purpose. Splicing a
            <Link> into a sentence emits React comment boundaries around the
            text expressions (`text<!-- --> <!-- --><a>`), which
            comment-stripping mobile webviews remove → React 19 #418. */}
        <p className="mt-3 text-[11px] leading-snug text-ink-200">
          {t('REVEAL.PRIVACY')}
        </p>
        <Link
          href={RedirectUrls.Disclosures}
          className="mt-0.5 inline-block text-[11px] text-ink-200 underline underline-offset-2 hover:text-ink-700"
        >
          {t('REVEAL.PRIVACY_LINK')}
        </Link>
      </form>
    </section>
  )
}

export default React.memo(RevealContact)
